<script setup lang="ts">
import { computed } from "vue";

import { Button, RollingLoader } from "@/components/global";
import { CircleCloseIcon, SmileyCheck } from "@/components/svg";

const emit = defineEmits(["dismiss"]);

const props = withDefaults(
  defineProps<{
    progress?: number;
    step?: string | null;
    status?: string | null;
    completionMessage?: string | null;
    usingPolling?: boolean;
    error?: string | null;
    dismissible?: boolean;
  }>(),
  {
    progress: 0,
    step: null,
    status: null,
    completionMessage: null,
    usingPolling: false,
    error: null,
    dismissible: false
  }
);

const progressValue = computed(() => {
  const value = Number(props.progress ?? 0);
  return Math.min(100, Math.max(0, Number.isNaN(value) ? 0 : value));
});

const isCompleted = computed(() => {
  const normalizedStatus = (props.status ?? "").toLowerCase();
  return normalizedStatus === "succeeded" || progressValue.value >= 100;
});

const titleText = computed(() => isCompleted.value ? "Modules setup completed" : "Setting up modules");

const stepText = computed(() => {
  if (isCompleted.value) {
    return props.completionMessage ?? props.step ?? "Workspace setup completed successfully.";
  }

  return props.step ?? "Initializing Setup...";
});

const errorText = computed(() => {
  if (!props.error) return "";
  return String(props.error).trim();
});
</script>

<template>
  <div class="module-setup-card">
    <Button
      v-if="dismissible && isCompleted"
      variant="naked"
      class="module-setup-dismiss"
      aria-label="dismiss-module-setup-notice"
      @click="emit('dismiss')"
    >
      <CircleCloseIcon />
    </Button>
    <div class="module-setup-icon flex-center">
      <RollingLoader v-if="!isCompleted" size="lg" variant="dark" />
      <SmileyCheck v-else />
    </div>
    <div class="module-setup-content">
      <h3 class="module-setup-title">{{ titleText }}</h3>
      <p class="module-setup-step">{{ stepText }}</p>
      <div class="module-setup-progress">
        <span class="module-setup-progress-bar" :style="{ width: `${progressValue}%` }"></span>
      </div>
      <div class="module-setup-meta">
        <span class="module-setup-percent">{{ progressValue }}%</span>
        <span  v-if="usingPolling" class="module-setup-pill">Using fallback</span>
      </div>
      <p v-if="!isCompleted && errorText" class="module-setup-error">{{ errorText }}</p>
    </div>
  </div>
</template>

<style scoped>
.module-setup-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem;
  border-radius: 0.75rem;
  background: #f7f9fc;
  border: 1px solid rgba(0, 0, 0, 0.04);
}

.module-setup-icon {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: rgba(49, 108, 214, 0.08);
}

.module-setup-content {
  flex: 1;
}

.module-setup-dismiss {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 1.5rem;
  height: 1.5rem;
  color: var(--ch-black);
  line-height: 100%;
}

.module-setup-title {
  margin: 0;
  font-size: var(--font-base);
  font-weight: 600;
  color: var(--ch-blue-100);
}

.module-setup-step {
  margin: 0.35rem 0 0.85rem;
  font-size: var(--font-sm);
  color: rgba(0, 0, 0, 0.6);
}

.module-setup-progress {
  width: 100%;
  height: 0.5rem;
  background: rgba(49, 108, 214, 0.08);
  border-radius: 999px;
  overflow: hidden;
}

.module-setup-progress-bar {
  display: block;
  height: 100%;
  background: var(--ch-blue-100);
  transition: width 0.35s ease;
}

.module-setup-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.5rem;
  font-size: var(--font-xs);
  color: rgba(0, 0, 0, 0.55);
}

.module-setup-percent {
  font-weight: 600;
  color: var(--ch-blue-100);
}

.module-setup-pill {
  background: rgba(255, 193, 7, 0.15);
  color: #b26a00;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  font-weight: 600;
}

.module-setup-error {
  margin-top: 0.75rem;
  color: #c43b2b;
  font-size: var(--font-xs);
}

@media screen and (max-width: 576px) {
  .module-setup-card {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
